import * as React from 'react';
import { connect } from 'react-redux';
import { isEqual, isNil } from 'lodash';

import Page from 'components/page/Page';
import PageNotFound from 'components/page/page-not-found/PageNotFound';
import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

import ContentListHeader from '../components/header/ContentListHeader';
import ContentListImageGrid from '../components/image-grid/ContentListImageGrid';

import {
  fetchSmartContentList,
  fullResetContentList
} from 'modules/lists-content/reducers/contentList';

import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import { getCollectionsSubnavigation } from 'utils/navigation';
import { updateQueryParams } from 'utils/location';
import { LIST_STATE_STORAGE_NAME } from 'utils/list';
import { getItem } from 'utils/storage/sessionStorage';
import { getQueryValueFromUrl } from 'utils/url';

import type {
  Breadcrumb,
  ContentListCollectionData,
  ContentListSmartType,
  ContentListStats,
  ContentListViewScreenState,
  LocationWindow
} from 'types';

applyTranslations(require('./content-lists-page.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

type OwnProps = {
  location: LocationWindow
};

type StateProps = {
  isLoading: boolean,
  hasMorePages: boolean,
  content: Array<ContentListCollectionData>,
  stats: ContentListStats
};

type DispatchProps = {
  dispatchResetContentList: () => Promise<void>,
  dispatchFetchContentLists: (b: number, a: string) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  page: number // not in the screenState due to infinite scroll
};

class ContentListViewPage extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      page: 1
    };
  }

  async componentDidMount() {
    const { dispatchResetContentList, dispatchFetchContentLists } = this.props;

    await dispatchResetContentList();
    await dispatchFetchContentLists(1, this.getScreenState().sortBy);
  }

  componentDidUpdate(prevProps: Props): void {
    const { dispatchFetchContentLists, location } = this.props;
    const newScreenState = this.getScreenState(location);
    const prevScreenState = this.getScreenState(prevProps.location);
    if (!isEqual(newScreenState, prevScreenState)) {
      dispatchFetchContentLists(1, newScreenState.sortBy);
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        page: 1
      });
    }
  }

  componentWillUnmount(): void {
    this.props.dispatchResetContentList();
  }

  getContentListType(location: LocationWindow): ContentListSmartType | undefined | null {
    const supportedTypes: Array<ContentListSmartType> = ['all_campaign_posts', 'all_highres'];
    const listId = location.params?.listId;

    if (!supportedTypes.includes(listId)) {
      return undefined;
    }

    return listId;
  }

  getScreenState(location: LocationWindow = this.props.location): ContentListViewScreenState {
    return {
      sortBy: getQueryValueFromUrl(location, 'sortBy') || 'campaign_start_date.desc'
    };
  }

  handleScreenStateUpdate = (newState: ContentListViewScreenState) => {
    const { location } = this.props;

    const newScreenState = {
      ...this.getScreenState(),
      ...newState
    };
    // will be propagated to state via getDerivedStateFromProps
    updateQueryParams(location, newScreenState);
  };

  handleLoadNextPage = () => {
    const { dispatchFetchContentLists, hasMorePages } = this.props;
    if (!hasMorePages) {
      return;
    }

    this.setState((state) => {
      const nextPage = state.page + 1;
      dispatchFetchContentLists(nextPage, this.getScreenState().sortBy);

      return {
        ...state,
        page: nextPage
      };
    });
  };

  render(): React.ReactNode {
    const { location, isLoading, hasMorePages, content, stats } = this.props;
    const orgId = location?.query?.orgId;
    const { listId } = location?.params || {};
    const contentListType = this.getContentListType(location);

    // if we have no organization id, or no list id or list id is not supported - show 404
    if (!orgId || !contentListType) {
      return <PageNotFound />;
    }

    const rootListURL = getItem(LIST_STATE_STORAGE_NAME) || `/collections/content?tab=smart&orgId=${orgId}`;

    const breadcrumbs: Array<Breadcrumb> = [{
      label: __t('content_lists_page.breadcrumbs_root_label'),
      link: rootListURL
    }, {
      label: __t(`content_lists_page.collection_name.smart_${String(contentListType)}`),
      link: ''
    }];

    return (
      <Page
        documentTitle={__t('content_lists_page.browser_title')}
        subnavigation={getCollectionsSubnavigation(orgId)}
      >
        <Breadcrumbs items={breadcrumbs} />
        <VerticalSpacing type="medium" />
        <ContentListHeader
          stats={stats}
          screenState={this.getScreenState()}
          onScreenStateUpdate={this.handleScreenStateUpdate}
        />
        <VerticalSpacing type="medium" />
        <ContentListImageGrid
          listId={listId}
          organizationId={Number(orgId)}
          hasPost={contentListType === 'all_campaign_posts'}
          hasMorePages={hasMorePages}
          isLoading={isLoading}
          content={content}
          onPageLoadTrigger={this.handleLoadNextPage}
        />
      </Page>
    );
  }
}

function mapStateToProps({ contentList }): StateProps {
  return {
    isLoading: contentList.isLoading,
    hasMorePages: contentList.hasMorePages,
    content: contentList.content,
    stats: contentList.stats
  };
}

function mapDispatchToProps(dispatch, ownProps: OwnProps): DispatchProps {
  return {
    dispatchResetContentList: () => dispatch(fullResetContentList()),
    dispatchFetchContentLists: (page: number, sortBy: string) => {
      const { listId } = ownProps.location?.params || {};
      const orgId = ownProps.location?.query?.orgId;

      if (isNil(orgId) || isNil(listId)) {
        logError('Cannot extract either orgId or listId from router params. Please check router setup');
        return Promise.resolve();
      }
      return dispatch(fetchSmartContentList(Number(orgId), listId.replace(/_/g, '-'), page, sortBy));
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ContentListViewPage);
